import { createContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import jwt from "jwt-decode";

import { decompressToken } from "../utils/decompressHelper";
import { isEmailAllowedOnUat, isUatPortal } from "../utils/uatAccess";

import {
  handleLogin,
  handleLogout,
  handlePasswordCreate,
  handlePasswordForgot,
  handlePasswordReset,
} from "../services/Authentication/AuthApis";

const UAT_LOGIN_DENIED_MESSAGE =
  "Access to the UAT portal is restricted. Please contact support if you need access.";

type AuthProps = {
  children: React.ReactNode; //👈 children prop type
};

export const AuthContext = createContext<any>({});

export const AuthProvider = ({ children }: AuthProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = location.pathname;

  const [loading, setLoading] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState(() => {
    const storedUser = localStorage.getItem("CurrentUser");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [currentGroup, setCurrentGroup] = useState(() => {
    const storedGroup = localStorage.getItem("CurrentGroupID");
    return storedGroup ? storedGroup : null;
  });
  const [isAdmin, setIsAdmin] = useState(false);

  const login = async (data: any) => {
    if (!isEmailAllowedOnUat(data?.Email)) {
      message.error(UAT_LOGIN_DENIED_MESSAGE);
      return;
    }

    setLoading(true);
    setCurrentUser(null);
    localStorage.clear();

    try {
      const result = await handleLogin(data);

      if (!result.success) {
        message.error(result.message);
        return;
      }

      const decoded = decompressToken(result.data.AccessToken);

      localStorage.setItem("AccessToken", result.data.AccessToken);
      localStorage.setItem("RefreshToken", result.data.RefreshToken);
      localStorage.setItem("CurrentUserID", decoded.id);

      if (decoded.Group.length > 0) {
        localStorage.setItem("CurrentGroupID", decoded.Group[0].id);
        setCurrentGroup(decoded.Group[0].id);
      }

      setCurrentUser(decoded);

      navigate("/dashboard");

      message.success(`Welcome back ${decoded.Name}`);
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async (data: any) => {
    if (!isEmailAllowedOnUat(data?.Email)) {
      message.error(UAT_LOGIN_DENIED_MESSAGE);
      return;
    }

    setLoading(true);
    setIsAdmin(false);
    setCurrentUser(null);

    localStorage.clear();
    try {
      const result = await handleLogin(data);

      if (!result.success) {
        message.error(result.message);
        return;
      }

      if (currentUrl === "/admin") {
        setIsAdmin(true);
      }

      const decoded = decompressToken(result.data.AccessToken);

      localStorage.setItem("AccessToken", result.data.AccessToken);
      localStorage.setItem("RefreshToken", result.data.RefreshToken);
      localStorage.setItem("CurrentUserID", decoded.id);

      if (decoded.Group.length > 0) {
        localStorage.setItem("CurrentGroupID", decoded.Group[0].id);
      }

      setCurrentUser(decoded);

      if (decoded.Group.length > 0) {
        setCurrentGroup(decoded.Group[0].id);
      }

      navigate("/admin/dashboard");

      message.success(`Welcome back ${decoded.Name}`);
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const createPassword = async (token: string, data: any) => {
    setLoading(true);

    try {
      const result = await handlePasswordCreate(token, data);

      if (!result.success) {
        message.error(result.message);
        return;
      }

      navigate("/");

      message.success("Successfully created password. Please login.");
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (data: any) => {
    setLoading(true);

    try {
      const result = await handlePasswordForgot(data);

      if (!result.success) {
        message.error(result.message);
        return;
      }

      navigate("/");

      message.success(
        "Email has been sent to reset your password. Please check your inbox."
      );
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token: string, data: any) => {
    setLoading(true);

    try {
      const result = await handlePasswordReset(token, data);

      if (!result.success) {
        message.error(result.message);
        return;
      }

      navigate("/");

      message.success("Successfully reset password. Please login.");
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setCurrentUser(null);

    localStorage.clear();
    try {
      const result = await handleLogout();

      if (!result.success) {
        message.error(result.message);
        return;
      }

      navigate("/");

      message.success("Successfully logged out.");
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const adminLogout = async () => {
    setLoading(true);
    setIsAdmin(false);
    setCurrentUser(null);

    localStorage.clear();
    try {
      const result = await handleLogout();

      if (!result.success) {
        message.error(result.message);
        return;
      }

      navigate("/admin");

      message.success("Successfully logged out.");
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUatPortal()) return;

    const hasSession =
      !!localStorage.getItem("AccessToken") || !!currentUser;
    if (!hasSession) return;

    const storedUser = localStorage.getItem("CurrentUser");
    const email =
      currentUser?.Email ??
      (storedUser ? JSON.parse(storedUser)?.Email : null);

    if (!isEmailAllowedOnUat(email)) {
      setCurrentUser(null);
      setCurrentGroup(null);
      setIsAdmin(false);
      localStorage.clear();
      const loginPath = currentUrl.startsWith("/admin") ? "/admin" : "/";
      if (currentUrl !== loginPath) {
        navigate(loginPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem("CurrentUser", JSON.stringify(currentUser));
      
      // Recover currentGroup if it's missing but user has groups
      if (!currentGroup && currentUser.Group && currentUser.Group.length > 0) {
        const recoveredGroupId = currentUser.Group[0].id;
        setCurrentGroup(recoveredGroupId);
        localStorage.setItem("CurrentGroupID", recoveredGroupId);
        console.log("Recovered currentGroup from currentUser:", recoveredGroupId);
      }
    } else {
      localStorage.removeItem("CurrentUser");
    }
  }, [currentUser, currentGroup]);

  useEffect(() => {
    if (currentGroup) {
      localStorage.setItem("CurrentGroupID", currentGroup);
    } else {
      localStorage.removeItem("CurrentGroupID");
    }
  }, [currentGroup]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentGroup,
        isAdmin,
        loading,

        login,
        adminLogin,
        createPassword,
        forgotPassword,
        resetPassword,
        logout,
        adminLogout,
        setLoading,
        setCurrentGroup,
        setCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
